/* FM Gallery (Alt) — rolling carousel behaviour.
   Motion model mirrors the marquee on fast-magic.com: a continuous roll plus
   drag-to-scrub with momentum that decays under friction. Driven by rAF (not a
   CSS animation) because drag has to inject velocity into the same transform.

   - Groups are widened by cloning until one exceeds the viewport, so the
     modulo wrap never exposes a gap.
   - Hovering pauses the roll and raises a cursor-following product card.
   - prefers-reduced-motion: no roll at all, native horizontal scroll instead. */

class FmCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.viewport = this.querySelector('.fm-carousel__viewport');
    this.groups = Array.from(this.querySelectorAll('[data-group]'));
    this.cursor = this.querySelector('[data-cursor]');
    this.cursorTitle = this.querySelector('[data-cursor-title]');
    this.cursorPrice = this.querySelector('[data-cursor-price]');
    if (!this.track || this.groups.length < 2) return;

    this.speed = parseFloat(this.dataset.speed) || 70;      // px per second
    this.friction = 0.94;                                    // momentum decay
    this.pauseOnHover = this.dataset.pauseOnHover !== 'false';
    this.fine = matchMedia('(hover: hover) and (pointer: fine)').matches;
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.offset = 0;
    this.velocity = 0;
    this.groupWidth = 0;
    this.hovering = false;
    this.dragging = false;
    this.dragMoved = 0;
    this.lastFrame = 0;

    ['measure', 'frame', 'onMove', 'onEnter', 'onLeave',
     'onDown', 'onDrag', 'onUp', 'onClick'].forEach((m) => { this[m] = this[m].bind(this); });

    this.whenImagesSettled(this.measure);
    this.measure();

    this._ro = new ResizeObserver(this.measure);
    this._ro.observe(this);
    // The header settles late (font swap shrinks it); main's offset feeds the
    // hero min-height, so re-measure whenever the header changes size.
    const headerSection = document.querySelector('.section-header');
    if (headerSection) this._ro.observe(headerSection);

    if (this.reduced) {
      this.viewport.style.overflowX = 'auto';
      return;
    }

    if (this.fine) this.bindHover();
    this.bindDrag();
    this._raf = requestAnimationFrame(this.frame);
  }

  disconnectedCallback() {
    if (this._ro) this._ro.disconnect();
    if (this._raf) cancelAnimationFrame(this._raf);
    document.removeEventListener('mousemove', this.onMove);
    window.removeEventListener('pointermove', this.onDrag);
    window.removeEventListener('pointerup', this.onUp);
  }

  whenImagesSettled(cb) {
    const pending = Array.from(this.querySelectorAll('img')).filter((i) => !i.complete);
    if (!pending.length) return;
    let left = pending.length;
    pending.forEach((i) => {
      const done = () => { if (--left === 0) cb(); };
      i.addEventListener('load', done, { once: true });
      i.addEventListener('error', done, { once: true });
    });
    setTimeout(cb, 2000); // never block forever on a stalled image
  }

  /* Clone items until one group is wider than the viewport, then cache width. */
  measure() {
    const container = this.offsetWidth;
    if (!container) return;

    // Item width is min(container, cap) — the main site's rule. Publish the
    // container's real content width so CSS can do that min() without 100vw,
    // which would overcount by the scrollbar.
    this.style.setProperty('--fm-carousel-vw', `${container}px`);

    // Hero sizing: tell CSS where main starts so the carousel view can fill
    // exactly one viewport below the header (footer stays past the fold).
    const main = this.closest('main[data-template-suffix]');
    if (main) {
      main.style.setProperty('--fm-main-top', `${Math.round(main.getBoundingClientRect().top + window.scrollY)}px`);
    }

    const first = this.groups[0];
    const originals = first.dataset.originalCount
      ? parseInt(first.dataset.originalCount, 10)
      : first.children.length;
    first.dataset.originalCount = originals;
    if (!originals) return;

    this.groups.forEach((g) => {
      while (g.children.length > originals) g.removeChild(g.lastElementChild);
    });

    let guard = 0;
    while (first.scrollWidth < container * 1.5 && guard < 40) {
      this.groups.forEach((g) => {
        for (let i = 0; i < originals; i += 1) {
          const clone = g.children[i].cloneNode(true);
          clone.setAttribute('tabindex', '-1');
          clone.setAttribute('aria-hidden', 'true');
          g.appendChild(clone);
        }
      });
      guard += 1;
    }

    this.groupWidth = first.scrollWidth;
  }

  frame(now) {
    this._raf = requestAnimationFrame(this.frame);
    if (!this.groupWidth) return;

    const dt = this.lastFrame ? Math.min((now - this.lastFrame) / 1000, 0.05) : 0;
    this.lastFrame = now;

    if (this.dragging) {
      // position is written directly by onDrag
    } else if (Math.abs(this.velocity) > 0.5) {
      this.offset += this.velocity;                 // momentum after release
      this.velocity *= this.friction;
    } else {
      this.velocity = 0;
      const paused = this.hovering && this.pauseOnHover;
      if (!paused) this.offset -= this.speed * dt;  // steady roll
    }

    this.wrap();
    this.track.style.transform = 'translate3d(' + this.offset.toFixed(2) + 'px,0,0)';
  }

  wrap() {
    const w = this.groupWidth;
    if (!w) return;
    if (this.offset <= -w) this.offset += w;
    else if (this.offset > 0) this.offset -= w;
  }

  /* ---- hover card ---- */
  bindHover() {
    this.addEventListener('mouseover', this.onEnter);
    this.addEventListener('mouseout', this.onLeave);
    document.addEventListener('mousemove', this.onMove, { passive: true });
  }

  onEnter(e) {
    const item = e.target.closest('.fm-carousel__item');
    if (!item || !this.contains(item)) return;
    this.hovering = true;
    this.cursorTitle.textContent = item.dataset.title || '';
    this.cursorPrice.textContent = item.dataset.price || '';
    this.placeCursor(e.clientX, e.clientY);   // position before revealing
    this.cursor.setAttribute('data-visible', 'true');
    if (this.pauseOnHover) this.setAttribute('data-paused', 'true');
  }

  onLeave(e) {
    const to = e.relatedTarget;
    if (to && this.contains(to) && to.closest('.fm-carousel__item')) return;
    this.hovering = false;
    this.cursor.removeAttribute('data-visible');
    this.removeAttribute('data-paused');
  }

  onMove(e) {
    if (this.cursor.getAttribute('data-visible') !== 'true') return;
    this.placeCursor(e.clientX, e.clientY);
  }

  placeCursor(cx, cy) {
    const pad = 14;
    const box = this.cursor.getBoundingClientRect();
    let x = cx + pad;
    let y = cy + pad;
    if (x + box.width > innerWidth - 8) x = cx - box.width - pad;
    if (y + box.height > innerHeight - 8) y = cy - box.height - pad;
    this.cursor.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0)';
  }

  /* ---- drag to scrub, with momentum ---- */
  bindDrag() {
    this.viewport.addEventListener('pointerdown', this.onDown);
    this.viewport.addEventListener('click', this.onClick, true);
    /* Kill native HTML5 drag-and-drop. Images and href'd anchors are both
       draggable by default, and onDown can't preventDefault the pointerdown
       (that's what keeps plain clicks navigating) — so the browser would
       sometimes decide the gesture was a picture drag and lift a ghost image
       instead of scrubbing the carousel. Racy by nature: native drag only
       engages a few px in, which is why it stuck only sometimes. */
    this.viewport.addEventListener('dragstart', (e) => e.preventDefault());
  }

  onDown(e) {
    if (e.button !== 0) return;
    this.dragging = true;
    this.dragMoved = 0;
    this.velocity = 0;
    this.lastX = e.clientX;
    /* No setPointerCapture here: capturing retargets pointerup to the
       viewport, so the click composes on the viewport instead of the product
       link and plain clicks never navigate. Move/up are tracked on window.
       is-dragging (which kills item pointer-events) waits for real movement
       for the same reason — added in onDrag, not here. */
    window.addEventListener('pointermove', this.onDrag, { passive: false });
    window.addEventListener('pointerup', this.onUp, { once: true });
    window.addEventListener('pointercancel', this.onUp, { once: true });
  }

  onDrag(e) {
    if (!this.dragging) return;
    const dx = e.clientX - this.lastX;
    this.lastX = e.clientX;
    this.dragMoved += Math.abs(dx);
    this.offset += dx;
    this.velocity = dx;                 // carried into momentum on release
    this.wrap();
    this.track.style.transform = 'translate3d(' + this.offset.toFixed(2) + 'px,0,0)';
    if (this.dragMoved > 3) {
      e.preventDefault();
      this.viewport.classList.add('is-dragging');
    }
  }

  onUp() {
    this.dragging = false;
    this.viewport.classList.remove('is-dragging');
    window.removeEventListener('pointermove', this.onDrag);
  }

  /* A drag that ends on a link must not navigate. */
  onClick(e) {
    if (this.dragMoved > 5) {
      e.preventDefault();
      e.stopPropagation();
      this.dragMoved = 0;
    }
  }
}

if (!customElements.get('fm-carousel')) {
  customElements.define('fm-carousel', FmCarousel);
}
